import { Search, X } from "lucide-react";
import React from "react";
import { cn } from "../../../lib/utils";
import { MySelect } from "../Select";

export type FilterSelectOption = {
  value: string;
  label: string;
};

export type DashboardFilter = {
  id: string;
  placeholder: string;
  value: string;
  options: FilterSelectOption[];
  onChange: (value: string) => void;
  showSearch?: boolean;
  className?: string;
};

export type DashboardFilterChip = {
  key: string;
  label: string;
  onRemove: () => void;
};

type DashboardFilterBarProps = {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  onClearSearch: () => void;
  onSearchSubmit?: () => void;
  searchPlaceholder?: string;
  filters?: DashboardFilter[];
  extraFilters?: React.ReactNode;
  chips?: DashboardFilterChip[];
  onClearAll?: () => void;
  resultCount?: number;
  resultLabel?: string;
  isLoading?: boolean;
  actions?: React.ReactNode;
};

export const ALL_OPTION: FilterSelectOption = { value: "all", label: "All" };

export const STATUS_FILTER_OPTIONS: FilterSelectOption[] = [
  ALL_OPTION,
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

export const ASSIGNMENT_FILTER_OPTIONS: FilterSelectOption[] = [
  ALL_OPTION,
  { value: "assigned", label: "Assigned" },
  { value: "unassigned", label: "Unassigned" },
];

export const TRIP_STATUS_FILTER_OPTIONS: FilterSelectOption[] = [
  ALL_OPTION,
  { value: "InTransit", label: "In transit" },
  { value: "Offline", label: "Offline" },
];

export const filterSelectClass =
  "h-[42px] min-w-[156px] text-[13px] px-3 text-hColor appearance-none bg-white border border-[#E5E5E5] rounded-lg focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary";

export const isAllFilter = (value?: string) => !value || value === "all";

export const boolFromToggleFilter = (
  value: string,
  trueValue: string,
  falseValue: string,
): boolean | undefined => {
  if (isAllFilter(value)) return undefined;
  if (value === trueValue) return true;
  if (value === falseValue) return false;
  return undefined;
};

export const stringFromFilter = (value?: string) =>
  isAllFilter(value) ? undefined : value;

const DashboardFilterBar: React.FC<DashboardFilterBarProps> = ({
  searchTerm,
  onSearchChange,
  onClearSearch,
  onSearchSubmit,
  searchPlaceholder = "Search",
  filters = [],
  extraFilters,
  chips = [],
  onClearAll,
  resultCount,
  resultLabel = "results",
  isLoading,
  actions,
}) => {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col xl:flex-row xl:items-center gap-3">
        <div className="relative w-full xl:max-w-[380px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
          <input
            type="text"
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                onSearchSubmit?.();
              }
            }}
            className="w-full text-[14px] pl-9 pr-10 py-2 border border-[#F0F0F0] bg-[#F6F6F6] rounded-full focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary h-[42px]"
          />
          {searchTerm && (
            <button
              type="button"
              onClick={onClearSearch}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-hColor"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3 flex-1">
          {extraFilters}
          {filters.map((filter) => (
            <MySelect
              key={filter.id}
              name={filter.id}
              id={filter.id}
              options={filter.options}
              getValue={(option) => option.value}
              getName={(option) => option.label}
              value={filter.value}
              onChange={filter.onChange}
              placeholder={filter.placeholder}
              disabled={isLoading}
              showSearch={filter.showSearch}
              className={cn(filterSelectClass, filter.className, {
                "opacity-50": isLoading,
              })}
            />
          ))}
          {chips.length > 0 && onClearAll && (
            <button
              type="button"
              onClick={onClearAll}
              className="text-[13px] font-medium text-primary hover:text-primary/80 px-1"
            >
              Clear all
            </button>
          )}
          {actions}
        </div>
      </div>

      {(chips.length > 0 || resultCount !== undefined) && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            {chips.map((chip) => (
              <button
                key={chip.key}
                type="button"
                onClick={chip.onRemove}
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#F6F6F6] text-[12px] text-hColor hover:bg-[#EFEFEF] transition-colors"
              >
                {chip.label}
                <X className="h-3 w-3 text-gray-500" />
              </button>
            ))}
          </div>
          {resultCount !== undefined && (
            <p className="text-[13px] text-gray-500">
              {resultCount} {resultLabel}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default DashboardFilterBar;
